import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import ModalErrorMessage from '../../components/modalErrorMessage/modalErrorMessage';
import Loading from '../../components/loading';
import ModalSuccessMessage from '../../components/modalSuccessMessage/modalSuccessMessage';
import { useExpenseCategories } from '../hooks/useExpenseCategories';
import { useSaveExpense } from '../hooks/useSaveExpense';
import { useExpense } from '../hooks/useExpense';

export default function ExpensesModal({ open, onClose, onSaveSuccess, expenseId }) {
  const [dueDate, setDueDate] = useState(dayjs());
  const [paymentDate, setPaymentDate] = useState(dayjs());
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [installments, setInstallments] = useState('');
  const [hasInstallments, setHasInstallments] = useState(false);
  const [observation, setObservation] = useState('');
  const [isPaid, setIsPaid] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState('');

  const { categories, refetch: refetchCategories, saveCategory } = useExpenseCategories();
  const { save: saveExpenseRequest } = useSaveExpense();
  const { expense, isLoading: isExpenseLoading } = useExpense(expenseId);

  const isEditing = Boolean(expenseId);
  const isLoading = isSaving || isExpenseLoading;

  const clearForm = () => {
    setDueDate(dayjs());
    setPaymentDate(dayjs());
    setAmount('');
    setDescription('');
    setInstallments('');
    setHasInstallments(false);
    setObservation('');
    setErrorMessage('');
    setCategoryId('');
    setIsPaid(false);
  };

  useEffect(() => {
    if (!expenseId) {
      clearForm();
    }
  }, [expenseId]);

  useEffect(() => {
    if (!expense) {
      return;
    }
    setDueDate(dayjs(expense.dueDate, 'YYYY-MM-DD'));
    setAmount(expense.amount);
    if (expense.paymentDate) {
      setIsPaid(true);
      setPaymentDate(dayjs(expense.paymentDate, 'YYYY-MM-DD'));
    }
    if (expense.installments > 0) {
      setInstallments(expense.installments);
      setHasInstallments(true);
    }
    setCategoryId(expense.categoryId);
    setDescription(expense.description);
    setObservation(expense.observation ?? '');
  }, [expense]);

  const saveNewCategory = () => {
    const categoryToSave = {
      description: newCategoryName,
    };
    setIsSaving(true);
    setOpenCategoryModal(false);
    saveCategory(categoryToSave)
      .then((returnFromApi) => {
        refetchCategories();
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
      })
      .catch((returnFromApi) => {
        console.error('ops! ocorreu um erro' + returnFromApi);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const saveExpenses = () => {
    setErrorMessage('');

    if (!amount || !dueDate || !categoryId || !description) {
      setShowErrorMessage(true);
      setErrorMessage('É obrigatório selecionar Data, Valor, Categoria e Descrição.');
      return;
    }

    const expensePayload = {
      id: expenseId,
      dueDate: dueDate.format('YYYY-MM-DD'),
      paymentDate: isPaid ? paymentDate.format('YYYY-MM-DD') : null,
      isPaid: isPaid,
      amount: parseFloat(amount),
      description: description,
      categoryId: categoryId,
      installments: hasInstallments ? installments : null,
      observation: observation,
    };

    setIsSaving(true);
    saveExpenseRequest(expensePayload)
      .then((returnFromApi) => {
        clearForm();
        onSaveSuccess(returnFromApi);
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Nova Saída</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Loading isToHidden={isLoading === false} />
            {showSuccessMessage && (
              <ModalSuccessMessage
                message={successMessage}
                show={showSuccessMessage}
                setShow={setShowSuccessMessage}
              />
            )}
            {showErrorMessage && (
              <ModalErrorMessage
                message={errorMessage}
                show={showErrorMessage}
                setShow={setShowErrorMessage}
              />
            )}

            <Grid size={6}>
              <DatePicker
                label="Data da Despesa"
                value={dueDate}
                onChange={(newValue) => setDueDate(newValue)}
                sx={{ width: '100%' }}
                format="DD/MM/YYYY"
              />
            </Grid>
            <Grid size={6}>
              <TextField
                data-cy="txt-value"
                label={hasInstallments ? 'Valor da parcela (R$)' : 'Valor (R$)'}
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasInstallments}
                    disabled={isEditing}
                    onChange={(e) => {
                      setHasInstallments(e.target.checked);
                      if (e.target.checked) setIsPaid(false);
                    }}
                  />
                }
                label="Com parcelamento"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPaid}
                    disabled={hasInstallments}
                    onChange={(e) => setIsPaid(e.target.checked)}
                  />
                }
                label="Marcar como paga"
              />
            </Grid>

            {hasInstallments && (
              <Grid size={12}>
                <TextField
                  disabled={isEditing}
                  label="Quantidade de parcelas"
                  type="number"
                  value={installments}
                  onChange={(e) => setInstallments(e.target.value)}
                  fullWidth
                  InputProps={{ inputProps: { min: 1 } }}
                />
              </Grid>
            )}

            {isPaid && (
              <Grid size={12}>
                <DatePicker
                  label="Data do Pagamento"
                  value={paymentDate}
                  onChange={(newValue) => setPaymentDate(newValue)}
                  sx={{ width: '100%' }}
                  format="DD/MM/YYYY"
                />
              </Grid>
            )}

            <Grid size={12}>
              <Grid container spacing={1} alignItems="center">
                <Grid size="grow">
                  <TextField
                    data-cy="select-category"
                    select
                    label="Categoria"
                    fullWidth
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                  >
                    {categories.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.description}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size="auto">
                  <Button variant="outlined" onClick={() => setOpenCategoryModal(true)}>
                    Nova Categoria
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid size={12}>
              <TextField
                data-cy="txt-description"
                label="Descrição"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
              />
            </Grid>

            <Grid size={12}>
              <TextField
                data-cy="txt-observation"
                label="Observação"
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            data-cy="btn-save"
            onClick={saveExpenses}
            variant="contained"
            disabled={isLoading}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCategoryModal}
        onClose={() => setOpenCategoryModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Nova Categoria</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome da Categoria"
            fullWidth
            variant="outlined"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryModal(false)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={saveNewCategory} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
